// The Internet OS symbol system. Every folder and page carries a small glyph
// that tries, with best intentions, to relate the thing's meaning to a mark.
// John curated the palette; we map the obvious kinds/names semantically and
// fall back to a STABLE pick (hashed off the name) so a given folder always
// wears the same symbol until the user overrides it by hand.

// Canonical palette — do not reorder casually; the hash fallback is positional,
// so reordering would silently re-symbol every un-overridden folder.
export const GLYPHS = [
  "◈", "◉", "⟁", "⎔", "❋", "✤", "◌", "⤴", "⌖", "◍", "⌬",
  "◐", "✵", "✳", "✦", "⌘", "⟐", "⟡", "⬡", "⧈", "⊙", "⋄",
] as const;

// Semantic anchors by node kind — the strong, legible associations.
const BY_KIND: Record<string, string> = {
  root: "⊙",
  section: "◍",
  project: "⬡",
  group: "⧈",
  dimension: "◍",
  artifact: "❋",
  task: "⌖",
  node: "✦",
  panel: "◐",
};

// Semantic anchors by node type / name fragment (checked as substrings, so
// "client" matches "clients"). First hit wins.
const BY_NAME: [RegExp, string][] = [
  [/person|people|stakeholder|client|founder|team|contact/, "◉"],
  [/decision|choice|verdict|ruling/, "◈"],
  [/concept|idea|theme|principle/, "✦"],
  [/vision|north.?star|mission|purpose/, "✵"],
  [/roadmap|plan|sequence|next|upcoming|phase/, "⤴"],
  [/product|feature|build|spec/, "⌬"],
  [/market|audience|growth|wedge/, "✤"],
  [/tech|system|architecture|stack|engine/, "⧈"],
  [/economic|cost|pricing|model|money|budget/, "⟐"],
  [/memory|personal|self|identity|profile/, "⊙"],
  [/security|privacy|trust|auth/, "⟁"],
  [/artifact|document|doc|paper|report/, "❋"],
  [/task|todo|loop|thread/, "⌖"],
  [/brand|design|visual|creative|aesthetic/, "⟡"],
];

function hashGlyph(seed: string): string {
  let h = 5381;
  for (let i = 0; i < seed.length; i++) h = ((h << 5) + h + seed.charCodeAt(i)) | 0;
  return GLYPHS[Math.abs(h) % GLYPHS.length];
}

export interface SymbolInput {
  kind: string;
  name: string;
  type?: string | null;
}

import { emojiFor } from "./emoji";

/** the kinds that are FOLDERS — a thing someone is building. These wear an
 *  emoji, always, unless a person picked a mark by hand (the override). */
const FOLDER_KINDS = new Set(["project", "group", "root", "section"]);

/** The symbol for a node. `override` (a user's hand-pick) always wins. A folder
 *  wears an emoji chosen from its name (lib/os/emoji); everything else keeps
 *  the geometric mark of its kind. */
export function symbolFor(input: SymbolInput, override?: string | null): string {
  if (override) return override;
  if (FOLDER_KINDS.has(input.kind)) return emojiFor(input.name, input.type);
  const hay = `${input.name} ${input.type ?? ""}`.toLowerCase();
  for (const [re, g] of BY_NAME) if (re.test(hay)) return g;
  if (input.type) {
    for (const [re, g] of BY_NAME) if (re.test(input.type.toLowerCase())) return g;
  }
  if (BY_KIND[input.kind]) return BY_KIND[input.kind];
  return hashGlyph(input.name || input.kind);
}

export default symbolFor;
